// Zero Mock Policy - Truth-Based Campaign Management
import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, Link, useLocation, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { UniversalErrorBoundary } from './components/ErrorBoundary/UniversalErrorBoundary';
import LoadingSpinner from './components/ui/LoadingSpinner';
import { ThemeProvider } from './components/theme-provider';
// Icons for ContentFlow and ExportCenter routes
import { Building2, Sparkles, Users, FileText } from 'lucide-react';

// Create QueryClient instance for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 5, // 5 minutes
      gcTime: 1000 * 60 * 10, // 10 minutes (formerly cacheTime)
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Lazy loaded pages for performance
const SolutionsPage = lazy(() => import('./pages/SolutionsPage').then(module => ({ default: module.SolutionsPage })));
const SolutionDetailPage = lazy(() => import('./pages/SolutionDetailPage').then(module => ({ default: module.SolutionDetailPage })));

// Auth pages — eager imports so they share the same AuthContext module instance as AuthProvider.
// Lazy-only auth routes can duplicate the context module in a separate chunk, making useContext(undefined)
// and triggering the "AuthProvider not configured" login error.
import SignInPage from './pages/auth/SignInPage';
import RegisterPage from './pages/auth/RegisterPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';
const ChangePasswordPage = lazy(() => import('./pages/settings/ChangePasswordPage'));
const ChangeEmailPage = lazy(() => import('./pages/settings/ChangeEmailPage'));
const ProfilePage = lazy(() => import('./pages/settings/ProfilePage'));
const GeneralSettingsPage = lazy(() => import('./pages/settings/GeneralSettingsPage'));
const BillingSettingsPage = lazy(() => import('./pages/settings/BillingSettingsPage'));
const UsageSettingsPage = lazy(() => import('./pages/settings/UsageSettingsPage'));
const UpgradeSettingsPage = lazy(() => import('./pages/settings/UpgradeSettingsPage'));
const CampaignManagementHubPage = lazy(() => import('./pages/campaigns/CampaignManagementHubPage'));
const CampaignCreatePage = lazy(() => import('./pages/campaigns/CampaignCreatePage'));
const CampaignGenerationPage = lazy(() => import('./pages/campaigns/CampaignGenerationPage'));
const EnhancedDataFoundationPage = lazy(() => import('./pages/data-foundation/EnhancedDataFoundationPage').then(module => ({ default: module.EnhancedDataFoundationPage })));
const TemplatesPage = lazy(() => import('./pages/TemplatesPage'));
const WorkflowPage = lazy(() => import('./pages/WorkflowPage'));
const WorkflowStep12Export = lazy(() => import('./pages/WorkflowStep12Export'));
const CitationAuditPanelPage = lazy(() => import('./components/citations/CitationAuditPanel').then(m => ({ default: m.CitationAuditPanelPage })));
const LLMModelSettings = lazy(() => import('./components/admin/LLMModelSettings').then(module => ({ default: module.LLMModelSettings })));
const WalkthroughAnalytics = lazy(() => import('./pages/Admin/WalkthroughAnalytics').then(module => ({ default: module.WalkthroughAnalytics })));
const AdminDashboardEnhancements = lazy(() => import('./components/admin/AdminDashboardEnhancements').then(module => ({ default: module.AdminDashboardEnhancements })));

// Executive Landing Page (/home)
const ExecutiveDashboard = lazy(() => import('./components/ExecutiveDashboard'));

// ORPHAN REMEDIATION: Previously implemented but never wired pages
const Epic27Monitor = lazy(() => import('./pages/Epic27Monitor'));
const AgentCoordinationDashboard = lazy(() => import('./pages/AgentCoordinationDashboard').then(module => ({ default: module.AgentCoordinationDashboard })));
const CommandCenter = lazy(() => import('./pages/CommandCenter'));
const SharedTaskBoard = lazy(() => import('./pages/SharedTaskBoard'));

// G3+G4: Governance Dashboard
const GovernanceDashboard = lazy(() => import('./pages/GovernanceDashboard'));

// CCE-006: Classification Review Queue (Human-in-the-loop content classification)
const ClassificationReviewQueue = lazy(() => import('./pages/ClassificationReviewQueue').then(module => ({ default: module.ClassificationReviewQueue })));

// CCE-009: Classification Settings (Taxonomy management)
const ClassificationSettings = lazy(() => import('./pages/ClassificationSettings').then(module => ({ default: module.ClassificationSettings })));

// EPIC 28: Content Sufficiency Dashboard (Wired Feb 5, 2026 - Integration Audit)
const ContentSufficiencyDashboard = lazy(() => import('./pages/ContentSufficiencyDashboard').then(module => ({ default: module.ContentSufficiencyDashboard })));
const PartnerManagementPage = lazy(() => import('./pages/PartnerManagementPage'));
const AuditLogPage = lazy(() => import('./pages/AuditLogPage'));
const PlatformAdminPage = lazy(() => import('./pages/PlatformAdminPage'));
const PartnerRegistrationPage = lazy(() => import('./pages/PartnerRegistrationPage'));

const PartnerDialoguePage = lazy(() => import('./pages/PartnerDialoguePage'));

const PartnerDashboard = lazy(() => import('./pages/PartnerDashboard'));

const CAMDashboard = lazy(() => import('./pages/CAMDashboard'));

const PartnerActivationPage = lazy(() => import('./pages/PartnerActivationPage'));

const SetPasswordPage = lazy(() => import('./pages/SetPasswordPage'));

const FunnelDashboard = lazy(() => import('./pages/FunnelDashboard'));

const PayoffScreen = lazy(() => import('./components/partner-questionnaire/PayoffScreen').then(m => ({ default: m.PayoffScreen })));
const InvitationAcceptancePage = lazy(() => import('./pages/invitations/InvitationAcceptancePage'));
const TeamInvitationsPage = lazy(() => import('./pages/settings/TeamInvitationsPage'));
const CampaignCollaboratorsPage = lazy(() => import('./pages/campaigns/CampaignCollaboratorsPage'));

// EU AI Act: Trust Report (Article 50(4) customer evidence pack)
const TrustReportPage = lazy(() => import('./pages/TrustReportPage'));

// HCLP Pages
const HCLPWorkspacePage = lazy(() => import('./pages/HCLPWorkspacePage'));
const PublicHCLPPage = lazy(() => import('./pages/PublicHCLPPage'));
const AccountContentTab = lazy(() => import('./pages/AccountContentTab'));
const HCLPReviewPage = lazy(() => import('./pages/HCLPReviewPage'));
const ArtifactViewPage = lazy(() => import('./pages/ArtifactViewPage'));

// Pipeline Visualization (Demo/Reference)
const PipelineVisualization = lazy(() => import('./pages/PipelineVisualization'));

// EPIC 3: Third-Party Integration Platform Connections (Settings UI - Story 3.7)
const IntegrationSettings = lazy(() => import('./pages/IntegrationSettings'));

// EPIC 24: E2E Guided Workflow Experience
import { 
  CommandPalette,
  A11yProvider,
  KeyboardNavProvider,
  // Story 24.6-24.9: Content Flow
  ContentFlow,
  // Story 24.48-24.52: Campaign Builder
  CampaignBuilder,
  // Story 24.53-24.56: Analytics Summary
  AnalyticsSummary,
  // Story 24.69-24.71: Export Center
  ExportCenter,
} from './components/epic24';
// TemplatesPage import removed (Lazy loaded)

// Story 24.35: Emma Persistent Interface
import { EmmaFirstLayout } from './layouts/EmmaFirstLayout';
import { EmmaContextProvider } from './contexts/EmmaContext';
import { EmmaStateProvider } from './context/EmmaStateContext';
import { OrganizationProvider } from './contexts/OrganizationContext';
import { TenantProvider } from './context/TenantContext';
import './components/emma/persistent-interface/persistent-interface.css';

// EPIC 30: Knowledge Management
import { KnowledgeManagementHub } from './components/knowledge/KnowledgeManagementHub';

import { ToastProvider } from './components/ToastProvider';
import { BackendHealthBanner } from './components/BackendHealthBanner';
import { RoutePolicyGuard } from './components/auth/RoutePolicyGuard';
import { ImpersonationBanner } from './components/auth/ImpersonationBanner';
import { FEATURES } from './config/features';
import { RequireAuth } from './components/auth/RequireAuth';
import { PublicOnlyRoute } from './components/auth/PublicOnlyRoute';
import { NavigateAppDefault } from './components/auth/NavigateAppDefault';
import ForbiddenPage from './pages/errors/ForbiddenPage';

// EPIC 8: Walkthrough System Components
import { WalkthroughProvider } from './contexts/WalkthroughProvider';
import { WalkthroughOverlay } from './components/walkthrough/WalkthroughOverlay';
import { DemoController } from './components/demo/DemoController';
// WalkthroughAnalytics import removed (Lazy loaded)

import './styles/mobile.css';

// Admin Components - LLM Configuration
// LLMModelSettings import removed (Lazy loaded)
// TODO: Install shadcn/ui components before enabling
// import { AdminDashboardEnhancements } from './components/admin/AdminDashboardEnhancements';

// DUPLICATE IMPORT REMOVED - WalkthroughOverlay already imported at line 49
// import { WalkthroughOverlay } from './components/WalkthroughOverlay';
import Dashboard from './pages/Dashboard';
import { AgentDiscoveryHub } from './components/agents/AgentDiscoveryHub';
import { Button } from './design-system/components/Button/Button';
import { CampaignRouter } from './components/campaign/CampaignRouter';
import { AdvancedContentQualityDashboard } from './components/content/AdvancedContentQualityDashboard';
import { UploadCenter } from './components/UploadCenter';
import { KnowledgeGraphStatus } from './components/KnowledgeGraphStatus';
import './index.css';

const AUTH_ROUTES = new Set([
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/verify-email',
  '/forbidden',
]);

const ConditionalBackendHealthBanner: React.FC = () => {
  const location = useLocation();
  if (AUTH_ROUTES.has(location.pathname)) {
    return null;
  }
  return <BackendHealthBanner />;
};

// CoSellus Logo Component using actual PNG file with accessibility enhancements
const CoSellusLogo = ({ width = 200, height = 50, className = "" }: { width?: number; height?: number; className?: string }) => (
  <img 
    src="/cosellus-logo.svg"
    width={width} 
    height={height} 
    alt="CoSellus - AI-Powered Marketing Intelligence Platform" 
    className={className}
    style={{
      objectFit: 'contain',
      filter: 'drop-shadow(0 2px 8px rgba(0, 74, 119, 0.2))'
    }}
    role="img"
    aria-label="CoSellus company logo and home page link"
  />
);

/** Authenticated shell: Emma layout + lazy route outlet + global overlays (no login/register in this tree). */
function ProtectedAppShell() {
  return (
    <>
      <EmmaFirstLayout>
        <ImpersonationBanner />
        <Suspense
          fallback={
            <div className="flex h-[50vh] items-center justify-center">
              <LoadingSpinner />
            </div>
          }
        >
          <Outlet />
        </Suspense>
      </EmmaFirstLayout>
      {FEATURES.EPIC_8_WALKTHROUGH && <WalkthroughOverlay />}
      <div className="fixed bottom-4 right-4 z-30">
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 transition-all">
          <CoSellusLogo width={28} height={19} className="opacity-60" />
          <span className="text-xs text-gray-600 font-medium">Powered by CoSellus</span>
        </div>
      </div>
      <CommandPalette />
    </>
  );
}

const PerformanceAnalyticsPage: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <h1 className="text-3xl font-bold text-gray-900">📊 Performance Analytics</h1>
          <p className="text-gray-600 mt-2">Monitor campaign performance and ROI insights</p>
        </div>
      </header>
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white p-6 rounded-lg shadow border">
            <h3 className="text-lg font-semibold mb-3">📈 Campaign Performance</h3>
            <div className="text-3xl font-bold text-blue-600 mb-2">87%</div>
            <p className="text-gray-600">Success Rate</p>
            <Button className="mt-4 w-full bg-blue-600 text-white hover:bg-blue-700">
              View Detailed Analytics
            </Button>
          </div>
          <div className="bg-white p-6 rounded-lg shadow border">
            <h3 className="text-lg font-semibold mb-3">💰 ROI Analysis</h3>
            <div className="text-3xl font-bold text-green-600 mb-2">340%</div>
            <p className="text-gray-600">Return on Investment</p>
            <Button className="mt-4 w-full bg-green-600 text-white hover:bg-green-700">
              Generate ROI Report
            </Button>
          </div>
          <div className="bg-white p-6 rounded-lg shadow border">
            <h3 className="text-lg font-semibold mb-3">🎯 Conversion Rates</h3>
            <div className="text-3xl font-bold text-purple-600 mb-2">23.4%</div>
            <p className="text-gray-600">Average Conversion</p>
            <Button className="mt-4 w-full bg-purple-600 text-white hover:bg-purple-700">
              Optimize Conversions
            </Button>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-xl font-bold mb-4">🚀 Quick Actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button 
              onClick={() => navigate('/content-generation')} 
              className="bg-green-600 text-white hover:bg-green-700"
            >
              ✨ Create Content
            </Button>
            <Button 
              onClick={() => navigate('/projects')} 
              className="bg-purple-600 text-white hover:bg-purple-700"
            >
              📊 View Campaigns
            </Button>
            <Button 
              onClick={() => navigate('/agents')} 
              className="bg-indigo-600 text-white hover:bg-indigo-700"
            >
              🤖 AI Agents
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

function App() {
  return (
    <ThemeProvider defaultTheme="system" storageKey="vite-ui-theme">
    <ToastProvider>
      <EmmaContextProvider>
        <EmmaStateProvider>
          <A11yProvider>
            <KeyboardNavProvider>
              <WalkthroughProvider>
                <div className="App">

              {/* EPIC 8 Sprint 2: Demo Controller (Story 8.2) */}
              {FEATURES.DEMO_MODE && <DemoController />}
              
              {/* EPIC 24: First-Time User Onboarding - DISABLED
                  Using in-app welcome experience inside dashboard/workflow surfaces instead.
                  This prevents duplicate onboarding modals. */}
              
              <QueryClientProvider client={queryClient}>
              <OrganizationProvider>
              <TenantProvider>
              <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <ConditionalBackendHealthBanner />
                <Suspense
                  fallback={
                    <div className="min-h-screen flex items-center justify-center bg-white">
                      <LoadingSpinner />
                    </div>
                  }
                >
                <Routes>
                  <Route path="/login" element={<PublicOnlyRoute><SignInPage /></PublicOnlyRoute>} />
                  <Route path="/register" element={<PublicOnlyRoute><RegisterPage /></PublicOnlyRoute>} />
                  <Route path="/registration-links/:token" element={<PartnerRegistrationPage />} />
                  <Route path="/magic-links/:token" element={<PartnerActivationPage />} />
                  <Route path="/p/:token" element={<PartnerDialoguePage />} />
                  <Route path="/brief/:token" element={<PublicHCLPPage />} />
                  <Route path="/public/hclp/:token" element={<PublicHCLPPage />} />
                  <Route path="/public/artifacts/:artifactId" element={<ArtifactViewPage />} />
                  <Route path="/hclp/artifacts/:artifactId/view" element={<ArtifactViewPage />} />
                  <Route path="/invitations/:invitationId" element={<InvitationAcceptancePage />} />
                  <Route path="/forgot-password" element={<PublicOnlyRoute><ForgotPasswordPage /></PublicOnlyRoute>} />
                  <Route path="/reset-password" element={<PublicOnlyRoute><ResetPasswordPage /></PublicOnlyRoute>} />
                  <Route path="/verify-email" element={<VerifyEmailPage />} />
                  <Route path="/forbidden" element={<ForbiddenPage />} />
                  <Route element={<RequireAuth />}>
                    <Route element={<ProtectedAppShell />}>
                  <Route path="/settings/password" element={<ChangePasswordPage />} />
                  <Route path="/settings/email" element={<ChangeEmailPage />} />
                  <Route path="/partner/set-password" element={<SetPasswordPage />} />
                  <Route path="/partner/payoff" element={<PayoffScreen partnerName="" answers={[]} />} />
                  <Route path="/partner/funnel" element={
                    <RoutePolicyGuard policy="authenticated" allowRoles={['org_admin', 'campaign_manager']}>
                      <FunnelDashboard />
                    </RoutePolicyGuard>
                  } />
                  <Route path="/change-password" element={<Navigate to="/settings/password" replace />} />
                  <Route path="/" element={<NavigateAppDefault />} />
                  <Route
                    path="/home"
                    element={
                      <RoutePolicyGuard policy="authenticated">
                        <ExecutiveDashboard />
                      </RoutePolicyGuard>
                    }
                  />
                  <Route
                    path="/dashboard"
                    element={
                      <RoutePolicyGuard policy="permission:project.view">
                        <Dashboard />
                      </RoutePolicyGuard>
                    }
                  />
                  <Route
                    path="/partners"
                    element={
                      <RoutePolicyGuard
                        policy="permission:agency.manage_assignments"
                        allowRoles={['agency_operator']}
                      >
                        <PartnerManagementPage />
                      </RoutePolicyGuard>
                    }
                  />
                  <Route
                    path="/partner/dashboard"
                    element={
                      <RoutePolicyGuard policy="authenticated" allowRoles={['partner_user']}>
                        <PartnerDashboard />
                      </RoutePolicyGuard>
                    }
                  />
                  <Route
                    path="/cam-dashboard"
                    element={
                      <RoutePolicyGuard
                        policy="authenticated"
                        allowRoles={['agency_operator', 'org_admin', 'campaign_manager', 'super_admin']}
                      >
                        <CAMDashboard />
                      </RoutePolicyGuard>
                    }
                  />
                  <Route
                    path="/audit-log"
                    element={
                      <RoutePolicyGuard policy="permission:analytics.view_audit">
                        <AuditLogPage />
                      </RoutePolicyGuard>
                    }
                  />
                  <Route
                    path="/platform-admin"
                    element={
                      <RoutePolicyGuard policy="permission:platform.manage_orgs">
                        <PlatformAdminPage />
                      </RoutePolicyGuard>
                    }
                  />
                  <Route path="/data-foundation" element={<EnhancedDataFoundationPage />} />
                  <Route path="/campaigns" element={<CampaignManagementHubPage />} />
                  <Route path="/campaigns/create" element={<CampaignCreatePage />} />
                  <Route path="/campaigns/:campaignId/generation" element={<CampaignGenerationPage />} />
                  <Route
                    path="/campaigns/:campaignId/collaborators"
                    element={
                      <RoutePolicyGuard policy="permission:campaign.manage_collaborators">
                        <CampaignCollaboratorsPage />
                      </RoutePolicyGuard>
                    }
                  />
                  
                  {/* SOL-001: Solution Management Dashboard (Sprint 2, Phases 2-4) */}
                  <Route path="/solutions" element={<SolutionsPage />} />
                  <Route path="/solutions/:id" element={<SolutionDetailPage />} />
                  
                  <Route path="/upload-center" element={<UploadCenter />} />
                  {/* Route alias for /upload → /upload-center (Jan 27, 2026) */}
                  <Route path="/upload" element={<Navigate to="/upload-center" replace />} />
                  {/* G3+G4: Governance Rules Dashboard */}
                  <Route path="/governance" element={<GovernanceDashboard />} />
                  <Route path="/knowledge-graph-status" element={<KnowledgeGraphStatus />} />
                  {/* Route split: /project-setup → /workflow (Jan 27, 2026) */}
                  <Route path="/project-setup" element={<Navigate to="/workflow" replace />} />

                  
                  {/* Admin Routes - Protected by admin auth */}
                  <Route
                    path="/admin"
                    element={
                      <RoutePolicyGuard policy="permission:org.manage_ai_config">
                        <AdminDashboardEnhancements />
                      </RoutePolicyGuard>
                    }
                  />
                  <Route path="/admin/llm-settings" element={
                    <RoutePolicyGuard policy="permission:org.manage_ai_config">
                      <div className="min-h-screen bg-gray-50 p-8">
                        <div className="max-w-7xl mx-auto">
                          <div className="mb-6">
                            <Link to="/admin" className="text-blue-600 hover:text-blue-700 text-sm">
                              ← Back to Admin Dashboard
                            </Link>
                          </div>
                          <LLMModelSettings />
                        </div>
                      </div>
                    </RoutePolicyGuard>
                  } />
                  <Route
                    path="/admin/walkthrough-analytics"
                    element={
                      <RoutePolicyGuard policy="permission:analytics.view_audit">
                        <WalkthroughAnalytics />
                      </RoutePolicyGuard>
                    }
                  />
                  
                  {/* EPIC 24: ARIA Templates - Story 24.72-24.74 */}
                  <Route path="/templates" element={<TemplatesPage />} />
                  
                  <Route path="/pipeline-visualization" element={<PipelineVisualization />} />
                  
                  {/* UX Overhaul: 12-Step Campaign Workflow */}
                  <Route path="/workflow" element={<WorkflowPage />} />
                  <Route path="/workflow/:step" element={<WorkflowPage />} />
                  <Route path="/workflow/:campaignId/:step" element={<WorkflowPage />} />
                  {/* Campaign-specific workflow routes (Jan 27, 2026) */}
                  <Route path="/campaign/:campaignId/workflow" element={<WorkflowPage />} />
                  <Route path="/campaign/:campaignId/workflow/:step" element={<WorkflowPage />} />
                  {/* Epic 4: Step 12 Export & Deploy (Feb 8, 2026) */}
                  <Route path="/campaigns/:campaignId/export" element={<WorkflowStep12Export />} />
                  {/* T2.4.5g.2: Citation Audit standalone route */}
                  <Route path="/campaigns/:campaignId/citation-audit" element={<CitationAuditPanelPage />} />
                  
                  {/* EU AI Act: Trust Report (Article 50(4) customer evidence pack) */}
                  <Route path="/campaigns/:campaignId/trust-report" element={
                    <RoutePolicyGuard policy="authenticated">
                      <TrustReportPage />
                    </RoutePolicyGuard>
                  } />
                  
                  {/* EPIC 30: Knowledge Management Hub */}
                  <Route path="/knowledge" element={
                    <KnowledgeManagementHub organizationId="current-org" />
                  } />
                  
                  {/* CCE-006: Classification Review Queue */}
                  <Route
                    path="/knowledge/review"
                    element={
                      <RoutePolicyGuard policy="permission:content.approve">
                        <ClassificationReviewQueue />
                      </RoutePolicyGuard>
                    }
                  />
                  
                  {/* CCE-018: Classification Review Queue (alternate route for ReviewQueueBadge) */}
                  <Route
                    path="/classification/review-queue"
                    element={
                      <RoutePolicyGuard policy="permission:content.approve">
                        <ClassificationReviewQueue />
                      </RoutePolicyGuard>
                    }
                  />
                  
                  {/* CCE-009: Classification Settings (Taxonomy Management) */}
                  <Route
                    path="/classification/settings"
                    element={
                      <RoutePolicyGuard policy="permission:content.approve">
                        <ClassificationSettings />
                      </RoutePolicyGuard>
                    }
                  />
                  
                  {/* EPIC 28: Content Sufficiency Dashboard (Wired Feb 5, 2026) */}
                  <Route
                    path="/content-sufficiency"
                    element={
                      <RoutePolicyGuard policy="permission:analytics.view">
                        <ContentSufficiencyDashboard />
                      </RoutePolicyGuard>
                    }
                  />
                  
                  {/* EPIC 24: Campaign Builder Standalone */}
                  <Route path="/campaign-builder" element={
                    <div className="p-8 bg-gray-50 min-h-screen">
                      <CampaignBuilder
                        onSave={() => {
                          // Standalone demo route keeps save handling local for now.
                        }}
                      />
                    </div>
                  } />
                  
                  {/* EPIC 24: Content Flow Editor */}
                  <Route path="/content-flow" element={
                    <div className="p-8 bg-gray-50 min-h-screen">
                      <h1 className="text-2xl font-bold mb-6">📝 Content Flow Editor</h1>
                      <ContentFlow
                        buckets={[
                          { id: 'company', label: 'Company', icon: Building2, color: 'from-blue-500 to-blue-600', items: [], description: 'Company overview' },
                          { id: 'product', label: 'Products', icon: Sparkles, color: 'from-purple-500 to-purple-600', items: [], description: 'Product features' },
                          { id: 'cases', label: 'Case Studies', icon: Users, color: 'from-green-500 to-green-600', items: [], description: 'Customer stories' },
                        ]}
                        onBucketClick={(id) => { /* handle bucket click */ }}
                      />
                    </div>
                  } />
                  
                  {/* EPIC 24: Analytics Summary Dashboard */}
                  <Route path="/analytics" element={
                    <RoutePolicyGuard policy="permission:analytics.view">
                      <div className="p-8 bg-gray-50 min-h-screen">
                        <h1 className="text-2xl font-bold mb-6">📊 Analytics Summary</h1>
                        <AnalyticsSummary
                          title="Analytics Summary"
                          metrics={[
                            { id: 'content_created', label: 'Content Created', value: 47, change: 12, trend: 'up' },
                            { id: 'campaigns_active', label: 'Campaigns Active', value: 8, change: 2, trend: 'up' },
                            {
                              id: 'engagement_rate',
                              label: 'Engagement Rate',
                              value: 23.4,
                              change: 5.2,
                              trend: 'up',
                              format: 'percentage',
                            },
                          ]}
                        />
                      </div>
                    </RoutePolicyGuard>
                  } />
                  
                  {/* EPIC 24: Export Center */}
                  <Route path="/export" element={
                    <div className="p-8 bg-gray-50 min-h-screen">
                      <h1 className="text-2xl font-bold mb-6">📤 Export Center</h1>
                      <ExportCenter
                        title="Export Your Content"
                        description="Download your content in various formats"
                        formats={[
                          { format: 'pdf', label: 'PDF Document', description: 'Export as formatted PDF', icon: FileText, available: true },
                          { format: 'csv', label: 'CSV Data', description: 'Export raw data as CSV', icon: FileText, available: true },
                          { format: 'html', label: 'HTML Email', description: 'Export as email-ready HTML', icon: FileText, available: true }
                        ]}
                        onExport={async (format) => { return ''; }}
                      />
                    </div>
                  } />
                  
                  {/* EPIC 27: System Health Monitoring - ORPHAN REMEDIATION */}
                  <Route
                    path="/health"
                    element={
                      <RoutePolicyGuard policy="permission:platform.database_ops">
                        <Epic27Monitor />
                      </RoutePolicyGuard>
                    }
                  />
                  
                  {/* ACE-009: Agent Coordination Dashboard - ORPHAN REMEDIATION */}
                  <Route path="/agent-coordination" element={<AgentCoordinationDashboard />} />
                  
                  {/* Command Center: AI Orchestration Hub - ORPHAN REMEDIATION */}
                  <Route
                    path="/command-center"
                    element={
                      <RoutePolicyGuard policy="permission:agent.execute">
                        <CommandCenter />
                      </RoutePolicyGuard>
                    }
                  />
                  
                  {/* ACE-008: Shared Task Board - ORPHAN REMEDIATION */}
                  <Route
                    path="/task-board"
                    element={
                      <RoutePolicyGuard policy="permission:campaign.view">
                        <SharedTaskBoard />
                      </RoutePolicyGuard>
                    }
                  />

                  {/* HCLP Routes */}
                  <Route path="/hclp/workspace" element={<HCLPWorkspacePage />} />
                  <Route path="/hclp/workspace/:campaignId" element={<HCLPWorkspacePage />} />
                  <Route path="/hclp/account/:accountId" element={<HCLPWorkspacePage />} />
                  <Route path="/hclp/review" element={<HCLPReviewPage />} />
                  <Route path="/upload/account" element={<AccountContentTab />} />
            {/* Orchestration - redirects to command center for now */}
            <Route path="/orchestration" element={<Navigate to="/command-center" replace />} />
            {/* Unified capability discovery - all agent routes redirect here */}
            <Route path="/agents" element={<Navigate to="/discover-agents" replace />} />
            <Route path="/agents/discover" element={<Navigate to="/discover-agents" replace />} />
            <Route
              path="/discover-agents"
              element={<AgentDiscoveryHub onAgentSelect={() => {}} />}
            />
            <Route path="/sofia" element={<Navigate to="/discover-agents" replace />} />
            <Route path="/casey" element={<Navigate to="/content-generation" replace />} />
            <Route path="/zara" element={<Navigate to="/performance-analytics" replace />} />
            <Route
              path="/settings"
              element={
                <RoutePolicyGuard policy="permission:org.edit_settings">
                  <GeneralSettingsPage />
                </RoutePolicyGuard>
              }
            />
            <Route
              path="/settings/team"
              element={
                <RoutePolicyGuard policy="permission:user.view_list">
                  <TeamInvitationsPage />
                </RoutePolicyGuard>
              }
            />
            <Route
              path="/settings/billing"
              element={
                <RoutePolicyGuard policy={['permission:org.view_billing', 'permission:subscription.view']}>
                  <BillingSettingsPage />
                </RoutePolicyGuard>
              }
            />
            <Route
              path="/settings/usage"
              element={
                <RoutePolicyGuard policy={['permission:credit.view_usage', 'permission:credit.view_partner_usage']}>
                  <UsageSettingsPage />
                </RoutePolicyGuard>
              }
            />
            <Route
              path="/settings/upgrade"
              element={
                <RoutePolicyGuard policy={['permission:org.change_plan', 'permission:subscription.manage']}>
                  <UpgradeSettingsPage />
                </RoutePolicyGuard>
              }
            />
            <Route
              path="/settings/integrations"
              element={
                <RoutePolicyGuard policy="permission:org.edit_settings">
                  <IntegrationSettings />
                </RoutePolicyGuard>
              }
            />
            <Route path="/subscription" element={<Navigate to="/settings/billing" replace />} />
            <Route path="/usage" element={<Navigate to="/settings/usage" replace />} />
            <Route path="/upgrade" element={<Navigate to="/settings/upgrade" replace />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/projects" element={<CampaignRouter userId="current-user" />} />
            <Route path="/content-generation" element={<AdvancedContentQualityDashboard />} />
            <Route
              path="/performance-analytics"
              element={
                <RoutePolicyGuard policy="permission:analytics.view">
                  <PerformanceAnalyticsPage />
                </RoutePolicyGuard>
              }
            />
            <Route path="*" element={<NavigateAppDefault />} />
                    </Route>
                  </Route>
                </Routes>
                </Suspense>
              </Router>
              </TenantProvider>
              </OrganizationProvider>
              </QueryClientProvider>
            </div>
          </WalkthroughProvider>
        </KeyboardNavProvider>
      </A11yProvider>
      </EmmaStateProvider>
            </EmmaContextProvider>
    </ToastProvider>
    </ThemeProvider>
  );
}

// ZERO-FAILURE WRAPPER - UniversalErrorBoundary catches ALL React errors
const AppWithErrorBoundary = () => (
  <UniversalErrorBoundary 
    fallback={(error: any, errorInfo: any) => (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 p-4">
        <div className="max-w-xl w-full bg-white shadow-xl rounded-xl p-6 border border-slate-200">
          <h1 className="text-xl font-bold text-red-600 mb-2 flex items-center gap-2">
            <span>⚠️ Application Error</span>
          </h1>
          <p className="text-slate-600 mb-4 text-xs font-medium">An unexpected error occurred during rendering.</p>
          {error && (
            <div className="mb-4 p-3.5 bg-red-50 border border-red-200 rounded-lg text-xs font-mono text-red-900 overflow-auto max-h-52 whitespace-pre-wrap">
              <div className="font-bold text-red-950 mb-1">{error.toString()}</div>
              {error.stack && <div className="text-[10px] text-red-700 mt-2 border-t border-red-200/60 pt-2">{error.stack}</div>}
            </div>
          )}
          <button 
            onClick={() => window.location.reload()}
            className="w-full bg-slate-900 text-white py-2.5 px-4 rounded-lg font-bold text-xs hover:bg-slate-800 transition-colors cursor-pointer"
          >
            Reload Application
          </button>
        </div>
      </div>
    )}
    onError={(error, errorInfo) => {
      console.error('🚨 CRITICAL ERROR CAUGHT:', error, errorInfo);
    }}
  >
    <App />
  </UniversalErrorBoundary>
);

export default AppWithErrorBoundary;

